import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { roleCenterIcon } from './roleCenterIcons';
import {
  deleteRole,
  fetchHistory,
  fetchRole,
  saveAccess,
  updateRole,
  type AccessChanges,
  type Catalog,
  type RoleChange,
  type RoleDetail,
  type RoleView,
} from './roleCenterApi';
import {
  changeLines,
  elementStatesWith,
  grantedWith,
  pendingCount,
  toggleChange,
} from './roleCenterRules';
import { DeleteRoleDialog, RoleFormDialog, SaveChangesDialog } from './RoleDialogs';

/**
 * Karta roli w centrum ról (decyzja użytkownika 22.10.2026, S-22.10-01):
 * zakładki „Uprawnienia” (pogrupowane przełączniki z opisem, co pozwalają
 * robić), „Co widzi” (pozycje menu, ekrany, sekcje i kafelki z ikonami
 * i opisami), „Użytkownicy” i „Historia”. Zmiany zbierają się na karcie,
 * a zapis idzie oknem z listą „było → jest” i powodem.
 */

type CardTab = 'permissions' | 'visibility' | 'users' | 'history';

export const ROLE_CARD_TABS: Array<{ key: CardTab; label: string }> = [
  { key: 'permissions', label: 'Uprawnienia' },
  { key: 'visibility', label: 'Co widzi' },
  { key: 'users', label: 'Użytkownicy' },
  { key: 'history', label: 'Historia' },
];

/** Nagłówek z ikoną roli — ten sam na liście kart i na karcie. */
export const RoleAvatar = ({ role, size = 40 }: { role: Pick<RoleView, 'icon' | 'locked'>; size?: number }) => (
  <Avatar sx={{ width: size, height: size, bgcolor: role.locked ? 'grey.700' : 'primary.main' }}>
    {roleCenterIcon(role.icon, { fontSize: size > 36 ? 'medium' : 'small' })}
  </Avatar>
);

const personName = (u: { firstName: string | null; lastName: string | null; username: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username;

export const RoleCard = ({
  roleId,
  catalog,
  roles,
  onBack,
  onChanged,
}: {
  roleId: string;
  catalog: Catalog;
  roles: RoleView[];
  onBack: () => void;
  onChanged: () => void;
}) => {
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<CardTab>('permissions');
  const [changes, setChanges] = useState<AccessChanges>({});
  const [saveOpen, setSaveOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [history, setHistory] = useState<RoleChange[] | null>(null);

  useEffect(() => {
    let alive = true;
    setRole(null);
    setChanges({});
    fetchRole(roleId)
      .then((r) => alive && setRole(r))
      .catch((e) => alive && setLoadError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [roleId]);

  useEffect(() => {
    if (tab !== 'history') return;
    let alive = true;
    fetchHistory(roleId)
      .then((h) => alive && setHistory(h))
      .catch(() => alive && setHistory([]));
    return () => {
      alive = false;
    };
  }, [tab, roleId, role]);

  const granted = useMemo(() => (role ? grantedWith(role, changes) : []), [role, changes]);
  const elements = useMemo(
    () => (role ? elementStatesWith(role, changes, catalog) : []),
    [role, changes, catalog],
  );
  const lines = useMemo(() => (role ? changeLines(role, changes, catalog) : []), [role, changes, catalog]);

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!role) return <CircularProgress size={24} />;

  const pending = pendingCount(changes);

  const save = async (reason: string) => {
    setSaving(true);
    setActionError(null);
    try {
      const updated = await saveAccess(role.id, changes, reason);
      setRole(updated);
      setChanges({});
      setSaveOpen(false);
      onChanged();
    } catch (e) {
      setActionError(toErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} data-testid="role-detail">
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ alignSelf: 'flex-start' }} data-testid="role-back">
        Wszystkie role
      </Button>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}>
            <RoleAvatar role={role} size={48} />
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" data-testid="role-detail-name">
                  {role.name}
                </Typography>
                {role.system ? <Chip size="small" label="rola systemowa" /> : <Chip size="small" color="primary" variant="outlined" label="rola własna" />}
                {!role.active ? <Chip size="small" color="warning" label="nieaktywna" /> : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {role.description ?? 'Bez opisu.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Kont z tą rolą: {role.userCount} · uprawnień: {role.permissions.length} z {role.permissionCells.length}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {role.id !== 'API_CLIENT' ? (
                <Button size="small" startIcon={<EditIcon />} onClick={() => setFormOpen(true)} data-testid="role-edit">
                  Opis i ikona
                </Button>
              ) : null}
              {!role.system ? (
                <Tooltip title={role.userCount > 0 ? 'Rolę mają konta — najpierw nadaj im inną rolę' : ''}>
                  <span>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={role.userCount > 0}
                      onClick={() => setDeleteOpen(true)}
                      data-testid="role-delete"
                    >
                      Usuń
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
          {role.locked ? (
            <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mt: 2 }} data-testid="role-lock-note">
              {role.lockReason}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {pending > 0 ? (
        <Alert
          severity="warning"
          data-testid="role-pending"
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<UndoIcon />} onClick={() => setChanges({})}>
                Cofnij
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  setActionError(null);
                  setSaveOpen(true);
                }}
                data-testid="role-save"
              >
                Zapisz zmiany ({pending})
              </Button>
            </Stack>
          }
        >
          Zmiany nie są jeszcze zapisane.
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v: CardTab) => setTab(v)}
        variant="scrollable"
        data-testid="role-detail-tabs"
      >
        {ROLE_CARD_TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} data-testid={`role-tab-${t.key}`} />
        ))}
      </Tabs>

      {tab === 'permissions' ? (
        <Stack spacing={2} data-testid="role-permissions">
          {catalog.permissionGroups.map((g) => (
            <Card key={g.key} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  {roleCenterIcon(g.icon, { color: 'primary' })}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {g.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.description}
                  </Typography>
                </Stack>
                {catalog.permissions
                  .filter((p) => p.group === g.key)
                  .map((p, i) => {
                    const cell = role.permissionCells.find((c) => c.key === p.key);
                    const on = granted.includes(p.key);
                    const changed = changes.permissions?.[p.key] !== undefined;
                    return (
                      <Box key={p.key} data-testid={`role-permission-${p.key}`}>
                        {i > 0 ? <Divider /> : null}
                        <Stack direction="row" spacing={1.5} sx={{ py: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ pt: 0.5, color: on ? 'primary.main' : 'text.disabled' }}>{roleCenterIcon(p.icon)}</Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {p.name}
                              {changed ? (
                                <Chip size="small" color="warning" label="zmienione" sx={{ ml: 1, height: 18 }} />
                              ) : null}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {p.description}
                            </Typography>
                            {cell?.locked && cell.lockReason && role.id === 'GUEST' ? (
                              <Typography variant="caption" color="text.secondary" data-testid={`role-permission-lock-${p.key}`}>
                                <LockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                {cell.lockReason}
                              </Typography>
                            ) : null}
                          </Box>
                          <Tooltip title={cell?.locked ? (cell.lockReason ?? '') : ''}>
                            <span>
                              <Switch
                                checked={on}
                                disabled={cell?.locked === true}
                                onChange={(e) =>
                                  setChanges(
                                    toggleChange(changes, 'permissions', p.key, e.target.checked, role.permissions.includes(p.key)),
                                  )
                                }
                                slotProps={{ input: { 'aria-label': p.name } }}
                              />
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : null}

      {tab === 'visibility' ? (
        <Stack spacing={2} data-testid="role-visibility">
          <Alert severity="info">{catalog.rules.hiddenNote}</Alert>
          {catalog.areas.map((a) => {
            const items = catalog.elements.filter((e) => e.area === a.key);
            if (items.length === 0) return null;
            return (
              <Card key={a.key} variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                    {roleCenterIcon(a.icon, { color: 'primary' })}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {a.name}
                    </Typography>
                  </Stack>
                  {items.map((e, i) => {
                    const state = elements.find((s) => s.key === e.key);
                    const changed = changes.visibility?.[e.key] !== undefined;
                    const savedVisible = !(role.elements.find((s) => s.key === e.key)?.hiddenExplicitly ?? false);
                    return (
                      <Box key={e.key} data-testid={`role-element-${e.key}`}>
                        {i > 0 ? <Divider /> : null}
                        <Stack direction="row" spacing={1.5} sx={{ py: 1, alignItems: 'flex-start', pl: 2 }}>
                          <Box sx={{ pt: 0.5, color: state?.visible ? 'primary.main' : 'text.disabled' }}>
                            {roleCenterIcon(e.icon)}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {e.name}{' '}
                              <Chip size="small" variant="outlined" label={catalog.kinds[e.kind] ?? e.kind} sx={{ height: 18 }} />
                              {changed ? <Chip size="small" color="warning" label="zmienione" sx={{ ml: 1, height: 18 }} /> : null}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {e.description}
                            </Typography>
                            {state?.locked && state.lockReason && !role.locked ? (
                              <Typography variant="caption" color="text.secondary" data-testid={`role-element-lock-${e.key}`}>
                                <LockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                {state.lockReason}
                              </Typography>
                            ) : null}
                          </Box>
                          <Tooltip title={state?.locked ? (state.lockReason ?? '') : ''}>
                            <span>
                              <Switch
                                checked={state?.visible === true}
                                disabled={state?.locked === true}
                                onChange={(ev) =>
                                  setChanges(toggleChange(changes, 'visibility', e.key, ev.target.checked, savedVisible))
                                }
                                slotProps={{ input: { 'aria-label': e.name } }}
                              />
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : null}

      {tab === 'users' ? (
        <Card variant="outlined" data-testid="role-users">
          <CardContent>
            {role.users.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Żadne konto nie ma tej roli.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Osoba</TableCell>
                    <TableCell>Nazwa użytkownika</TableCell>
                    <TableCell>Spółka</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {role.users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Link to={`/users/${u.id}`} data-testid={`role-user-${u.id}`}>
                          {personName(u)}
                        </Link>
                      </TableCell>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>{u.company ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : null}

      {tab === 'history' ? <HistoryTable rows={history} testId="role-history" /> : null}

      {saveOpen ? (
        <SaveChangesDialog
          open
          role={role}
          lines={lines}
          saving={saving}
          error={actionError}
          onClose={() => setSaveOpen(false)}
          onSave={(reason) => void save(reason)}
        />
      ) : null}
      {formOpen ? (
        <RoleFormDialog
          open
          mode="edit"
          role={role}
          roles={roles}
          icons={catalog.icons}
          saving={saving}
          error={actionError}
          onClose={() => setFormOpen(false)}
          onSave={async (values, reason) => {
            setSaving(true);
            setActionError(null);
            try {
              const updated = await updateRole(role.id, {
                ...(role.system ? {} : { name: values.name.trim(), active: values.active }),
                description: values.description.trim() || null,
                icon: values.icon,
                reason,
              });
              setRole(updated);
              setFormOpen(false);
              onChanged();
            } catch (e) {
              setActionError(toErrorMessage(e));
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : null}
      {deleteOpen ? (
        <DeleteRoleDialog
          open
          role={role}
          saving={saving}
          error={actionError}
          onClose={() => setDeleteOpen(false)}
          onDelete={async (reason) => {
            setSaving(true);
            setActionError(null);
            try {
              await deleteRole(role.id, reason);
              setDeleteOpen(false);
              onChanged();
              onBack();
            } catch (e) {
              setActionError(toErrorMessage(e));
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : null}
    </Stack>
  );
};

/** Historia zmian — karta roli i całe centrum. */
export const HistoryTable = ({
  rows,
  testId,
  showRole = false,
}: {
  rows: RoleChange[] | null;
  testId: string;
  showRole?: boolean;
}) => (
  <Card variant="outlined" data-testid={testId}>
    <CardContent>
      {rows === null ? (
        <CircularProgress size={20} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Bez zmian — role działają tak, jak je zasiano z dzisiejszych uprawnień.
        </Typography>
      ) : (
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '15%' }}>Kiedy</TableCell>
              <TableCell sx={{ width: '13%' }}>Kto</TableCell>
              {showRole ? <TableCell sx={{ width: '14%' }}>Rola</TableCell> : null}
              <TableCell sx={{ width: showRole ? '22%' : '28%' }}>Co</TableCell>
              <TableCell sx={{ width: '16%' }}>Było → jest</TableCell>
              <TableCell>Powód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{fmtDate(r.changedAt)}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{r.changedByName ?? '—'}</TableCell>
                {showRole ? <TableCell sx={{ wordBreak: 'break-word' }}>{r.roleName}</TableCell> : null}
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {r.kind === 'permission' ? 'Uprawnienie: ' : r.kind === 'visibility' ? 'Widoczność: ' : ''}
                  {r.subjectName}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {r.oldValue ?? '—'} → {r.newValue ?? '—'}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{r.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);
